/**
 * Telehealth / non-face-to-face helpers (kept dependency-free so state-machine,
 * mapping and auto-start can all import them without a cycle). Re-exported from
 * `./mapping` and therefore from the package index.
 */

/** Loose shape accepted by {@link isFaceToFace} (domain `Session`, raw DynamoDB item, or partial). */
export interface FaceToFaceLike {
  faceToFace?: unknown
  nonFaceToFaceSessionType?: string | null
}

/**
 * Placeholder values AHP (or a data-entry default) may put in
 * `non_face_to_face_session_type` that do NOT mean "telehealth".
 */
const PLACEHOLDER_SUB_TYPES: ReadonlySet<string> = new Set([
  'n/a',
  'na',
  'none',
  'null',
  '-',
  'in person',
  'in-person',
  'face to face',
  'face-to-face',
  'f2f',
])

/** True when `value` is a real telehealth sub-type (non-empty and not a placeholder). */
export function hasTelehealthSubType(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const v = value.trim().toLowerCase()
  return v !== '' && !PLACEHOLDER_SUB_TYPES.has(v)
}

/**
 * Single source of truth for "is this an in-person session?".
 *
 * Default is TRUE unless AHP (or a stored copy) explicitly says otherwise:
 * `false`, `'false'`, `0` and `'0'` are non-face-to-face, and so is any
 * non-empty, non-placeholder `non_face_to_face_session_type` (AHP sends
 * 'Phone Call', 'video', …; 'n/a', 'none', 'in person' … are ignored).
 */
export function isFaceToFace(session: FaceToFaceLike | null | undefined): boolean {
  if (!session) return true
  const flag = session.faceToFace
  if (flag === false || flag === 0) return false
  if (typeof flag === 'string') {
    const f = flag.trim().toLowerCase()
    if (f === 'false' || f === '0') return false
  }
  return !hasTelehealthSubType(session.nonFaceToFaceSessionType)
}

export type TelehealthKind = 'phone' | 'video' | 'telehealth'

/**
 * Sub-type of a non-face-to-face session, for copy. 'Phone Call' -> phone;
 * anything mentioning video / zoom / teams -> video; everything else (including
 * a missing sub-type) -> generic 'telehealth'.
 */
export function telehealthKind(
  session: { nonFaceToFaceSessionType?: string | null } | null | undefined,
): TelehealthKind {
  const value = session?.nonFaceToFaceSessionType
  if (!hasTelehealthSubType(value)) return 'telehealth'
  const raw = value.trim().toLowerCase()
  if (/video|zoom|teams/.test(raw)) return 'video'
  if (raw.includes('phone')) return 'phone'
  return 'telehealth'
}
